import React from 'react';
import { Loader2 } from 'lucide-react';
import {
  ButtonVariant,
  SizeVariant,
  IconPosition,
  BaseComponentProps,
  DisableableProps,
  LoadableProps,
  ClickableProps,
  WithChildrenProps,
} from '../types/componentThemes';

export interface ButtonProps
  extends BaseComponentProps,
    DisableableProps,
    LoadableProps,
    ClickableProps,
    WithChildrenProps {
  /** Button style variant */
  variant?: ButtonVariant;
  /** Button size */
  size?: SizeVariant;
  /** Icon element to display */
  icon?: React.ReactNode;
  /** Position of the icon relative to text */
  iconPosition?: IconPosition;
  /** Whether button should take full width */
  fullWidth?: boolean;
  /** Button type attribute */
  type?: 'button' | 'submit' | 'reset';
  /** ARIA label for accessibility */
  ariaLabel?: string;
}

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  icon,
  iconPosition = 'left',
  fullWidth = false,
  type = 'button',
  className = '',
  style,
  testId,
  ariaLabel,
  onClick,
  children,
}) => {
  const baseClasses = [
    'inline-flex',
    'items-center',
    'justify-center',
    'gap-2',
    'font-medium',
    'rounded-lg',
    'transition-all',
    'duration-200',
    'focus:outline-none',
    'focus:ring-2',
    'focus:ring-offset-2',
    'disabled:opacity-50',
    'disabled:cursor-not-allowed',
  ];

  const variantClasses: Record<ButtonVariant, string[]> = {
    primary: [
      'bg-gradient-to-r',
      'from-primary-600',
      'to-primary-700',
      'text-white',
      'hover:from-primary-700',
      'hover:to-primary-800',
      'focus:ring-primary-500',
      'active:scale-95',
      'shadow-sm',
      'hover:shadow-md',
    ],
    secondary: [
      'bg-gradient-to-r',
      'from-secondary-600',
      'to-secondary-700',
      'text-white',
      'hover:from-secondary-700',
      'hover:to-secondary-800',
      'focus:ring-secondary-500',
      'active:scale-95',
      'shadow-sm',
      'hover:shadow-md',
    ],
    outline: [
      'bg-transparent',
      'border',
      'border-neutral-300',
      'dark:border-neutral-600',
      'text-neutral-700',
      'dark:text-neutral-200',
      'hover:bg-neutral-50',
      'dark:hover:bg-neutral-800',
      'hover:border-primary-400',
      'dark:hover:border-primary-500',
      'hover:text-primary-700',
      'dark:hover:text-primary-300',
      'focus:ring-primary-500',
      'active:scale-95',
    ],
    ghost: [
      'bg-transparent',
      'text-neutral-700',
      'dark:text-neutral-200',
      'hover:bg-neutral-100',
      'dark:hover:bg-neutral-800',
      'focus:ring-neutral-500',
      'active:scale-95',
    ],
    danger: [
      'bg-gradient-to-r',
      'from-error',
      'to-error-dark',
      'text-white',
      'hover:from-error-dark',
      'hover:to-error-dark',
      'focus:ring-error-DEFAULT',
      'active:scale-95',
      'shadow-sm',
      'hover:shadow-md',
    ],
  };

  const sizeClasses: Record<SizeVariant, string[]> = {
    sm: ['h-8', 'px-3', 'text-sm'],
    md: ['h-10', 'px-4', 'text-sm'],
    lg: ['h-12', 'px-6', 'text-base'],
  };

  const widthClasses = fullWidth ? ['w-full'] : [];

  const buttonClasses = [
    ...baseClasses,
    ...variantClasses[variant],
    ...sizeClasses[size],
    ...widthClasses,
    className,
  ].join(' ');

  const isDisabled = disabled || loading;

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!isDisabled && onClick) {
      onClick();
    }
  };

  const renderIcon = () => {
    if (loading) {
      return <Loader2 className="w-4 h-4 animate-spin" />;
    }
    return icon;
  };

  const renderContent = () => {
    const iconElement = renderIcon();
    const hasIcon = iconElement !== undefined && iconElement !== null;
    const hasChildren = children !== undefined && children !== null;

    if (hasIcon && !hasChildren) return iconElement;
    if (!hasIcon && hasChildren) return children;

    if (hasIcon && hasChildren) {
      if (iconPosition === 'left') {
        return (
          <>
            {iconElement}
            {children}
          </>
        );
      } else {
        return (
          <>
            {children}
            {iconElement}
          </>
        );
      }
    }

    return null;
  };

  return (
    <button
      type={type}
      className={buttonClasses}
      disabled={isDisabled}
      onClick={handleClick}
      style={style}
      data-testid={testId}
      aria-label={ariaLabel}
      aria-disabled={isDisabled}
      aria-busy={loading}
    >
      {renderContent()}
    </button>
  );
};
